import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Autocomplete,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import { ApiRequest } from 'GlobalFunctions';

interface SafetyAlertSenderProps {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

export default function SafetyAlertSender({ open, onClose, onSent }: SafetyAlertSenderProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [targetType, setTargetType] = useState('all');
  const [targetValue, setTargetValue] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (open) loadUsers();
  }, [open]);

  const loadUsers = async () => {
    await ApiRequest({
      url: '/users/list',
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      query: { full_list: true },
      setResponse: (response: any) => {
        setUsers(response.list || []);
      },
    });
  };

  const handleSend = async () => {
    setSending(true);
    await ApiRequest({
      url: '/safety-alerts/send',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: {
        target_type: targetType,
        target_value: targetType === 'all' ? [] : targetValue.map((u: any) => u.user_id),
        message: message || undefined,
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Alerta enviada exitosamente', { variant: 'success' });
          resetForm();
          onSent();
        } else {
          enqueueSnackbar(response.message || 'Error al enviar', { variant: 'error' });
        }
        setSending(false);
      },
    });
  };

  const resetForm = () => {
    setTargetType('all');
    setTargetValue([]);
    setMessage('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar Alerta Manual</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} style={{ marginTop: 5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Destinatarios</InputLabel>
              <Select
                value={targetType}
                label="Destinatarios"
                onChange={(e) => { setTargetType(e.target.value); setTargetValue([]); }}
              >
                <MenuItem value="all">Todos los usuarios</MenuItem>
                <MenuItem value="user">Usuarios específicos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {targetType === 'user' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option: any) => `${option.name} ${option.last_name || ''}`}
                value={targetValue}
                onChange={(_, newValue) => setTargetValue(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Seleccionar usuarios" size="small" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option: any, index: number) => (
                    <Chip
                      label={`${option.name} ${option.last_name || ''}`}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Mensaje (opcional)"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={sending || (targetType === 'user' && targetValue.length === 0)}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
